import Decimal from "decimal.js";

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;

export interface BudgetAllocationState {
  scale: Decimal;
  balance: Decimal;
  usage: Decimal;
  available: Decimal;
  budgeted: Decimal;
  readyToAssign: Decimal;
  totalAvailable: Decimal;
  totalAllocation: Decimal;
  categoryOverspending: Decimal;
}

export type BudgetAllocationSummaryState =
  | "fully-assigned"
  | "ready"
  | "over-assigned";

export interface BudgetAllocationSummary {
  state: BudgetAllocationSummaryState;
  primaryAmount: Decimal;
  readyToAssign: Decimal;
  overAssigned: Decimal;
  totalAvailable: Decimal;
  totalAllocation: Decimal;
  cashShortage: Decimal;
  balance: Decimal;
  categoryOverspending: Decimal;
}

export interface BudgetAllocationSegment {
  x: Decimal;
  width: Decimal;
}

export interface BudgetAllocationSegments {
  activity: BudgetAllocationSegment;
  available: BudgetAllocationSegment;
  overspent: BudgetAllocationSegment;
}

export function decimalFromRaw(value: string | undefined): Decimal {
  if (!value) {
    return new Decimal(0);
  }

  try {
    return new Decimal(value);
  } catch {
    return new Decimal(0);
  }
}

export function decimalFromDisplay(
  value: string | undefined,
  group: string,
  decimal: string,
): Decimal {
  if (!value) {
    return new Decimal(0);
  }

  let normalized = value;
  if (group) {
    normalized = normalized.replaceAll(group, "");
  }
  if (decimal && decimal !== ".") {
    normalized = normalized.replace(decimal, ".");
  }

  return decimalFromRaw(normalized);
}

export function percent(value: Decimal, scale: Decimal): number {
  if (scale.lte(0)) {
    return MIN_PERCENT;
  }

  const valuePercent = value.div(scale).mul(100);
  if (valuePercent.lt(0)) {
    return MIN_PERCENT;
  }
  if (valuePercent.gt(MAX_PERCENT)) {
    return MAX_PERCENT;
  }

  return valuePercent.toDecimalPlaces(2).toNumber();
}

export function clampBudgeted(
  value: Decimal,
  state: BudgetAllocationState,
): Decimal {
  if (state.scale.lte(0)) {
    return state.budgeted.toDecimalPlaces(2);
  }

  const minBudgetedValue = minBudgeted(state);
  if (value.lt(minBudgetedValue)) {
    return minBudgetedValue;
  }

  const maxBudgetedValue = maxBudgeted(state);
  if (value.gt(maxBudgetedValue)) {
    return maxBudgetedValue;
  }

  return value.toDecimalPlaces(2);
}

export function minBudgeted(state: BudgetAllocationState): Decimal {
  return Decimal.min(state.budgeted, 0).toDecimalPlaces(2);
}

export function maxBudgeted(state: BudgetAllocationState): Decimal {
  return positiveAmount(
    state.budgeted
      .add(overspentForAvailable(state.available))
      .add(positiveAmount(state.readyToAssign)),
  ).toDecimalPlaces(2);
}

export function minAvailable(state: BudgetAllocationState): Decimal {
  return Decimal.min(
    availableForBudgeted(minBudgeted(state), state.usage),
    state.available,
  ).toDecimalPlaces(2);
}

export function maxAvailable(state: BudgetAllocationState): Decimal {
  return Decimal.max(
    availableForBudgeted(maxBudgeted(state), state.usage),
    state.available,
  ).toDecimalPlaces(2);
}

export function availableForBudgeted(
  budgeted: Decimal,
  usage: Decimal,
): Decimal {
  return budgeted.sub(usage).toDecimalPlaces(2);
}

export function budgetedDelta(
  budgeted: Decimal,
  state: BudgetAllocationState,
): Decimal {
  return budgeted.sub(state.budgeted).toDecimalPlaces(2);
}

export function totalAvailableForAvailable(
  available: Decimal,
  state: BudgetAllocationState,
): Decimal {
  return state.totalAvailable
    .sub(positiveAmount(state.available))
    .add(positiveAmount(available))
    .toDecimalPlaces(2);
}

export function totalAvailableDeltaForAvailable(
  available: Decimal,
  state: BudgetAllocationState,
): Decimal {
  return totalAvailableForAvailable(available, state)
    .sub(state.totalAvailable)
    .toDecimalPlaces(2);
}

export function readyToAssignForTotalAvailable(
  totalAvailable: Decimal,
  state: BudgetAllocationState,
): Decimal {
  return positiveAmount(state.balance.sub(totalAvailable)).toDecimalPlaces(2);
}

export function overAssignedForTotalAvailable(
  totalAvailable: Decimal,
  state: BudgetAllocationState,
): Decimal {
  return positiveAmount(
    totalAvailable.sub(positiveAmount(state.balance)),
  ).toDecimalPlaces(2);
}

export function totalAllocationForBudgeted(
  budgeted: Decimal,
  state: BudgetAllocationState,
): Decimal {
  return state.totalAllocation
    .sub(state.budgeted)
    .add(budgeted)
    .toDecimalPlaces(2);
}

export function categoryOverspendingForAvailable(
  available: Decimal,
  state: BudgetAllocationState,
): Decimal {
  return positiveAmount(
    state.categoryOverspending
      .sub(overspentForAvailable(state.available))
      .add(overspentForAvailable(available)),
  ).toDecimalPlaces(2);
}

export function cashShortageForBalance(balance: Decimal): Decimal {
  return positiveAmount(balance.neg()).toDecimalPlaces(2);
}

export function allocationSummaryFor(
  available: Decimal,
  budgeted: Decimal,
  state: BudgetAllocationState,
): BudgetAllocationSummary {
  const totalAvailable = totalAvailableForAvailable(available, state);
  const readyToAssign = readyToAssignForTotalAvailable(totalAvailable, state);
  const overAssigned = overAssignedForTotalAvailable(totalAvailable, state);
  const allocationState: BudgetAllocationSummaryState = overAssigned.gt(0)
    ? "over-assigned"
    : readyToAssign.gt(0)
      ? "ready"
      : "fully-assigned";

  return {
    state: allocationState,
    primaryAmount: overAssigned.gt(0)
      ? overAssigned
      : readyToAssign,
    readyToAssign,
    overAssigned,
    totalAvailable,
    totalAllocation: totalAllocationForBudgeted(budgeted, state),
    cashShortage: cashShortageForBalance(state.balance),
    balance: state.balance.toDecimalPlaces(2),
    categoryOverspending: categoryOverspendingForAvailable(available, state),
  };
}

export function segmentsFor(
  state: BudgetAllocationState,
  available: Decimal,
  budgeted: Decimal,
): BudgetAllocationSegments {
  const activityAmount = positiveAmount(state.usage);
  const envelopeAmount = positiveAmount(budgeted);
  const activityWithinEnvelope = Decimal.min(activityAmount, envelopeAmount);

  return {
    activity: {
      x: new Decimal(0),
      width: activityWithinEnvelope,
    },
    available: {
      x: activityWithinEnvelope,
      width: positiveAmount(available),
    },
    overspent: {
      x: envelopeAmount,
      width: overspentForAvailable(available),
    },
  };
}

export function overspentForAvailable(available: Decimal): Decimal {
  return positiveAmount(available.neg());
}

export function keyboardStep(
  key: string,
  state: BudgetAllocationState,
  currentBudgeted: Decimal,
): Decimal {
  const step = new Decimal(1);

  switch (key) {
    case "ArrowRight":
    case "ArrowUp":
      return step;
    case "ArrowLeft":
    case "ArrowDown":
      return step.neg();
    case "PageUp":
      return step.mul(10);
    case "PageDown":
      return step.mul(-10);
    case "Home":
      return minBudgeted(state).sub(currentBudgeted);
    case "End":
      return maxBudgeted(state).sub(currentBudgeted);
    default:
      return new Decimal(0);
  }
}

export function positiveAmount(value: Decimal): Decimal {
  if (value.gt(0)) {
    return value;
  }

  return new Decimal(0);
}
